// Story 7.1 (ADO-904) — breadcrumb trail (Figma "Category Listing" breadcrumb:
// Shop › Bread & Cakes › In House Bakery › Bakery). Lets the user step back up
// the Department › Category › Sub-Category hierarchy without losing their place.
//
// Epic 7 pixel parity (ADO-903) — prototype breadcrumb chrome: 14px semibold
// crumbs, 16px muted caret separators, secondary links → accent on hover, and a
// primary-text (non-link) current crumb. Below 768px the trail collapses (CSS
// only — one accessible DOM) to the prototype's mobile variant: back-chevron +
// the PARENT crumb as the only link + the truncated current label.

import { Icon } from '../../design-system';
import type { CategoryNode } from '../../api/browse.client';
import { formatCategoryName } from './categoryDisplay';
import styles from './Breadcrumb.module.css';

export interface BreadcrumbProps {
  /** Root-first trail (Department → … → current node). */
  readonly trail: readonly CategoryNode[];
  /** Navigate to a crumb; `null` is the "Shop" root (clears the selection). */
  readonly onNavigate: (node: CategoryNode | null) => void;
  /** Story 7.12 Iteration 2 — a final, non-navigable current crumb (the PDP's
   *  product name). When set it becomes the `aria-current="page"` crumb. */
  readonly currentLabel?: string;
}

// Story 7.15 (ADO-1372): the `staticTrail` prop is GONE. It existed for exactly one
// consumer — the PDP, whose ancestor crumbs were inert FIXTURE LABELS because the
// product's category path was not on the read model (gap G6). AC2 put the real
// nodes on the read model, so the PDP now passes them as `trail` and the prop had
// no consumer left. A prop with no consumer is dead code, so it was deleted rather
// than kept; `currentLabel` stays, because the product name genuinely has no node.
export function Breadcrumb({ trail, onNavigate, currentLabel }: BreadcrumbProps) {
  // One flat item list ("Shop" root + the trail) so the mobile CSS variant can
  // address the PARENT crumb (second-to-last) positionally in the markup.
  const items: readonly {
    readonly key: string;
    readonly name: string;
    /** `null` is the "Shop" root. */
    readonly node: CategoryNode | null;
  }[] = [
    { key: 'shop-root', name: 'Shop', node: null },
    // Display casing only (2026-07-17 demo feedback): the customer hierarchy
    // is ALL UPPERCASE; render Title Case like the prototype.
    ...trail.map((node) => ({ key: node.id, name: formatCategoryName(node.name), node })),
    ...(currentLabel === undefined ? [] : [{ key: 'current', name: currentLabel, node: null }]),
  ];

  // <768px only ONE step-back crumb + the current label stay visible; the rest
  // hide (visual truncation — the DOM/a11y trail is unchanged above 768px).
  //
  // Story 7.15 (ADO-1372): this is `length - 2` again, and it is now SOUND. It was
  // a scan for the last NAVIGABLE crumb because the PDP's trail was
  // [Shop, <inert category>, <inert sub-category>, product] — positional keying put
  // the back chevron on an inert `<span>`, so at 390px the breadcrumb rendered with
  // ZERO focusable elements and a phone user had no in-page route back. Both inert
  // ancestor kinds are gone: every item except the LAST is a real node (or the
  // "Shop" root), and the last can never be picked here. The narrowest case,
  // [Shop, product] for an uncategorised product, resolves to "Shop" — still
  // focusable, so the 390px guard holds at every trail length.
  const backIndex = items.length - 2;

  return (
    <nav aria-label="Breadcrumb" className={styles.breadcrumb}>
      <ol className={styles.list}>
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isParent = index === backIndex;
          const itemClass = [
            styles.item,
            !isLast && !isParent ? styles.itemAncestor : '',
            isLast ? styles.itemCurrent : '',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <li key={item.key} className={itemClass}>
              {index > 0 && <Icon name="caretRight" size={16} className={styles.separator} />}
              {isLast ? (
                <span className={styles.current} aria-current="page">
                  {item.name}
                </span>
              ) : (
                // Story 7.15 (ADO-1372): there is no third, inert-`<span>` branch
                // any more. It rendered the PDP's fixture ancestor labels (G6), and
                // with real navigable nodes on the read model every non-last crumb
                // is a button — an unreachable branch is worse than none.
                <button
                  type="button"
                  className={styles.crumb}
                  onClick={() => onNavigate(item.node)}
                >
                  {isParent && <Icon name="caretLeft" size={16} className={styles.backChevron} />}
                  {item.name}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
